import React, { createContext, useCallback, useContext, useMemo, useReducer, useState } from 'react';
import cartReducer from '../../reducers/cartReducer';
import addCartItem from '../../actions/ADD_CART_ITEM';
import setCartItem from '../../actions/SET_CART_ITEM';
import delCartItem from '../../actions/DEL_CART_ITEM';
import reloadCartItems from '../../actions/RELOAD_CART_ITEMS';
import { LoadingContext } from './LoadingContext';
import { node } from 'prop-types';
import useIdempotency from '../../hooks/useIdempotency';


const COULDNT_UPDATE_ERROR_MESSAGE = 'Couldn\'t update cart.';
const INVALID_CHECKOUT_ERROR_MESSAGE = 'Invalid checkout.';

export const CartContext = createContext({});

// The initial state of the cart context
const INITIAL_STATE = []; // eslint-disable-line no-undef

// The app context provider component
const CartContextProvider = ({ children }) => {
    // defaultCartContext is returned by the server on the initial render
    const [checkoutData, setCheckoutData] = useState({});
    const [cartState, cartDispatch] = useReducer(cartReducer, INITIAL_STATE);
    const [showCartOverlay, setShowCartOverlay] = useState(null);
    // Loading state
    const {
        startLoading,
        finishLoading,
    } = useContext(LoadingContext);
    const [cartUpdating, setCartUpdating] = useState(false);
    const idempotency = useIdempotency();
    const reloadLineItems = useCallback((lineItems = []) => {
        // Reload all items
        cartDispatch(
            reloadCartItems(lineItems.map((lineItem) => ({
                lineItemId: lineItem.id,
                inventoryLevelId: lineItem.inventoryLevelId,
                title: lineItem.title,
                imageUrl: lineItem.imageUrl ?? null,
                quantity: lineItem.currentQuantity,
                price: lineItem.totalUnitPrice,
                availableForSale: lineItem.availableForSale,
                availableQuantity: lineItem.availableQuantity,
                sku: lineItem.sku,
                handle: lineItem.productHandle ?? null,
                bypassNegativeQuantity: lineItem.bypassNegativeQuantity,
            })))
        );
    }, []);
    // Add item to checkout (if there's a valid session)
    const addLineItem = useCallback(async (inventoryLevelId, quantity, lineItems, onFinish = () => {}) => {
        try {
            startLoading();
            setCartUpdating(true);
            let body = null;
            if (lineItems) {
                body = { lineItems };
            } else {
                body = {
                    inventoryLevelId,
                    quantity,
                };
            }

            // Execute the request
            const data = await idempotency.execute(
                new URL('/api/checkouts/lineItems', process.env.APP_URL),
                body,
                'POST',
                {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                }
            );

            // If there's a checkout, update the cart

            if (data?.checkout) {
                if (Array.isArray(lineItems) && lineItems.length > 0) {
                    cartDispatch(reloadLineItems(data.checkout.LineItems));
                } else {
                    const newLineItem =
                        data.checkout.LineItems
                            .filter((lineItem) => lineItem.inventoryLevelId === parseInt(inventoryLevelId))
                            ?.at(0) ?? null;
                    // Add item
                    setCheckoutData(data.checkout);
                    if (newLineItem) {
                        cartDispatch(addCartItem(inventoryLevelId, quantity, newLineItem.title, newLineItem.imageUrl, newLineItem.totalUnitPrice, newLineItem.availableQuantity, newLineItem.availableForSale, newLineItem.sku, newLineItem.productHandle, newLineItem.id, newLineItem.bypassNegativeQuantity));
                    }
                }
            } else {
                throw new Error(COULDNT_UPDATE_ERROR_MESSAGE);
            }
            onFinish();
            return { success: true };
        } catch (e) {
            console.error(e);
            return {
                success: false,
                error: e.message,
            };
        } finally {
            finishLoading();
            setCartUpdating(false);
        }
    }, [startLoading, finishLoading, reloadLineItems]);
    // Set a checkout item quantity if the item exists in the checkout and the session checkout is valid
    const setLineItem = useCallback(async (inventoryLevelId, quantity) => {
        try {
            startLoading();
            setCartUpdating(true);

            // Execute the request
            const data = await idempotency.execute(
                new URL('/api/checkouts/lineItems', process.env.APP_URL),
                {
                    inventoryLevelId,
                    quantity,
                },
                'PUT',
                {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                }
            );

            // If there's a checkout, update the cart

            if (data.checkout) {
                // Update item
                setCheckoutData(data.checkout);
                cartDispatch(setCartItem(inventoryLevelId, quantity));
            }
        } catch (e) {
            console.error(e);
        } finally {
            finishLoading();
            setCartUpdating(false);
        }
    }, [startLoading, finishLoading]);
    // Delete an item from the checkout
    const delLineItem = useCallback(async (lineItemId) => {
        try {
            startLoading();
            setCartUpdating(true);

            // Execute the request
            const data = await idempotency.execute(
                new URL(`/api/checkouts/lineItems/${lineItemId}`, process.env.APP_URL),
                null,
                'DELETE',
                {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                }
            );

            // If there's a checkout, update the cart

            if (data.checkout) {
                // Delete item
                setCheckoutData(data.checkout);
                cartDispatch(delCartItem(lineItemId));
            }
        } catch (e) {
            console.error(e);
        } finally {
            finishLoading();
            setCartUpdating(false);
        }
    }, [startLoading, finishLoading]);
    // Fetch checkout data
    const fetchCheckoutData = useCallback(async () => {
        try {
            const response = await fetch(new URL('api/checkouts', process.env.APP_URL));
            const data = await response.json();
            if (data.checkout) {
                setCheckoutData(data.checkout);
                reloadLineItems(data.checkout.LineItems);
            }
        } catch (e) {
            console.error(e);
        }
    }, [ reloadLineItems ]);
    // Add a discount code to the checkout
    const applyDiscountCode = useCallback(async (discountCode) => {
        try {
            startLoading();
            setCartUpdating(true);
            const accessCode = checkoutData?.webUrl?.split('/').pop() ?? null;
            if (accessCode === null) {
                throw new Error(INVALID_CHECKOUT_ERROR_MESSAGE);
            }
            const url = new URL(`/api/checkouts/${accessCode}/discountCodes`, process.env.APP_URL);
            const params = new URLSearchParams({
                code: discountCode,
            });

            // Execute the request
            const data = await idempotency.execute(
                `${url}?${params}`,
                null,
                'POST',
                {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                }
            );

            // If there's a checkout, update the cart

            if (data?.checkout) {
                setCheckoutData(data.checkout);
            } else {
                throw new Error(COULDNT_UPDATE_ERROR_MESSAGE);
            }
            return { success: true };
        } catch (e) {
            console.error(e);
            return {
                success: false,
                error: e.message,
            };
        } finally {
            finishLoading();
            setCartUpdating(false);
        }
    }, [checkoutData, finishLoading, startLoading]);
    // Remove a discount code from the checkout
    const removeDiscountCode = useCallback(async (discountCode) => {
        try {
            startLoading();
            setCartUpdating(true);
            const accessCode = checkoutData?.webUrl?.split('/').pop() ?? null;
            if (accessCode === null) {
                throw new Error(INVALID_CHECKOUT_ERROR_MESSAGE);
            }
            const url = new URL(`/api/checkouts/${accessCode}/discountCodes`, process.env.APP_URL);
            const params = new URLSearchParams({
                code: discountCode,
            });

            // Execute the request
            const data = await idempotency.execute(
                `${url}?${params}`,
                null,
                'DELETE',
                {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                }
            );

            // If there's a checkout, update the cart

            if (data?.checkout) {
                setCheckoutData(data.checkout);
            } else {
                throw new Error(COULDNT_UPDATE_ERROR_MESSAGE);
            }
            return { success: true };
        } catch (e) {
            console.error(e);
            return {
                success: false,
                error: e.message,
            };
        } finally {
            finishLoading();
            setCartUpdating(false);
        }
    }, [checkoutData, finishLoading, startLoading]);

    // Estimate shipping for the checkout
    const estimateShippingAndShipping = useCallback(async (stateId) => {
        try {
            const accessCode = checkoutData?.webUrl?.split('/').pop() ?? null;
            if (accessCode === null) {
                throw new Error(INVALID_CHECKOUT_ERROR_MESSAGE);
            }
            const url = new URL(`/api/checkouts/${accessCode}/estimateShipping`, process.env.APP_URL);
            url.searchParams.append('stateId', stateId);
            const response = await fetch(url);
            const data = await response.json();
            if (data.error) {
                throw new Error(data.error);
            }
            return {
                success: true,
                shipping: data.shipping,
                taxRate: data.taxRate,
            };
        } catch (e) {
            return {
                success: false,
                error: e.message,
            };
        }
    }, [checkoutData]);
    const contextValue = useMemo(() => ({
        cartState,
        checkoutData,
        cartUpdating,
        showCartOverlay,
        addLineItem,
        setLineItem,
        delLineItem,
        reloadLineItems,
        fetchCheckoutData,
        setShowCartOverlay,
        applyDiscountCode,
        removeDiscountCode,
        estimateShippingAndShipping,
    }), [
        cartState,
        checkoutData,
        cartUpdating,
        showCartOverlay,
        addLineItem,
        setLineItem,
        delLineItem,
        reloadLineItems,
        fetchCheckoutData,
        setShowCartOverlay,
        applyDiscountCode,
        removeDiscountCode,
        estimateShippingAndShipping,
    ]);
    return (<CartContext.Provider
        value={contextValue}>
        {children}
    </CartContext.Provider>);
};

CartContextProvider.propTypes = {
    children: node,
};

export default CartContextProvider;
